import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useRestaurant } from '../../context/RestaurantContext';
import { 
  ChefHat, Clock, AlertTriangle, CheckCircle2, Bell, 
  Volume2, VolumeX, Maximize2, Minimize2, RefreshCw,
  Filter, Settings, Flame, Printer
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import KitchenOrderCard from './kitchen/KitchenOrderCard';
import StationSelector from './kitchen/StationSelector';
import { printKitchenTicket, printKitchenTicketsByStation } from '../../utils/printing';

// Time thresholds in seconds
const TIME_THRESHOLDS = {
  warning: 300,  // 5 minutes - yellow
  urgent: 600,   // 10 minutes - red
  critical: 900  // 15 minutes - flashing
};

export default function Kitchen() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { selectedRestaurant } = useRestaurant();
  
  // Orders state
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [selectedStation, setSelectedStation] = useState('all');
  const [stations, setStations] = useState(['all']);
  const [showCompleted, setShowCompleted] = useState(false);
  
  // UI state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Audio ref for new order notification
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);

  // Play a beep sound using Web Audio API
  const playNotificationSound = useCallback(() => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.value = 800; // Hz
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.5);
    } catch (e) {
      console.log('Audio notification not supported:', e);
    }
  }, []);

  // Fetch kitchen orders
  const fetchOrders = useCallback(async () => {
    if (!selectedRestaurant) return;
    
    try {
      const params = new URLSearchParams();
      if (selectedStation !== 'all') {
        params.append('station', selectedStation);
      }
      if (!showCompleted) {
        params.append('status', 'pending,confirmed,preparing');
      }
      
      const response = await api.get(
        `/restaurants/${selectedRestaurant.id}/kitchen-orders?${params.toString()}`
      );
      
      const newOrders = response.data;
      
      // Check for new orders and play sound
      if (soundEnabled && orders.length > 0) {
        const newOrderIds = newOrders.map(o => o.id);
        const existingOrderIds = orders.map(o => o.id);
        const hasNewOrders = newOrderIds.some(id => !existingOrderIds.includes(id));
        
        if (hasNewOrders) {
          playNotificationSound();
          toast.info('New order received!', { icon: '🔔' });
        }
      }
      
      setOrders(newOrders);
      
      // Extract unique stations from orders
      const orderStations = [...new Set(newOrders.flatMap(o => 
        o.items?.map(i => i.station).filter(Boolean) || []
      ))];
      if (orderStations.length > 0) {
        setStations(['all', ...orderStations]);
      }
    } catch (error) {
      console.error('Failed to fetch kitchen orders:', error);
    } finally {
      setLoading(false);
    }
  }, [api, selectedRestaurant, selectedStation, showCompleted, soundEnabled, orders.length, playNotificationSound]);

  // Initial fetch and auto-refresh
  useEffect(() => {
    fetchOrders();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchOrders, 10000); // Refresh every 10 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchOrders, autoRefresh]);

  // Bump order (mark as ready)
  const handleBumpOrder = async (orderId) => {
    try {
      await api.put(`/orders/${orderId}/status`, { status: 'ready' });
      toast.success('Order marked as ready');
      fetchOrders();
    } catch (error) {
      console.error('Failed to bump order:', error);
      toast.error('Failed to update order');
    }
  };

  // Start preparing order
  const handleStartOrder = async (orderId) => {
    try {
      await api.put(`/orders/${orderId}/status`, { status: 'preparing' });
      toast.success('Order started');
      fetchOrders();
    } catch (error) {
      console.error('Failed to start order:', error);
      toast.error('Failed to update order');
    }
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Get order counts by status
  const pendingCount = orders.filter(o => o.status === 'pending' || o.status === 'confirmed').length;
  const preparingCount = orders.filter(o => o.status === 'preparing').length;
  const readyCount = orders.filter(o => o.status === 'ready').length;

  if (!selectedRestaurant) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <ChefHat className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No restaurant selected</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isFullscreen ? 'fixed inset-0 z-50' : ''} bg-slate-900`}>
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <ChefHat className="w-6 h-6 text-orange-500" />
              <h1 className="text-xl font-bold text-white" data-testid="kitchen-title">
                Kitchen Display
              </h1>
            </div>
            
            {/* Status Badges */}
            <div className="flex items-center gap-2">
              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                <Clock className="w-3 h-3 mr-1" />
                {pendingCount} New
              </Badge>
              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                <Flame className="w-3 h-3 mr-1" />
                {preparingCount} Preparing
              </Badge>
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                {readyCount} Ready
              </Badge>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Station Filter */}
            <StationSelector
              stations={stations}
              selected={selectedStation}
              onSelect={setSelectedStation}
            />
            
            {/* Sound Toggle */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`border-slate-600 ${soundEnabled ? 'text-green-400' : 'text-slate-400'}`}
              data-testid="sound-toggle"
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </Button>
            
            {/* Auto Refresh Toggle */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`border-slate-600 ${autoRefresh ? 'text-green-400' : 'text-slate-400'}`}
              data-testid="auto-refresh-toggle"
            >
              <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} style={{ animationDuration: '3s' }} />
            </Button>
            
            {/* Manual Refresh */}
            <Button
              variant="outline"
              size="icon"
              onClick={fetchOrders}
              className="border-slate-600 text-slate-300 hover:text-white"
              data-testid="manual-refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            
            {/* Fullscreen Toggle */}
            <Button
              variant="outline"
              size="icon"
              onClick={toggleFullscreen}
              className="border-slate-600 text-slate-300 hover:text-white"
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-slate-800 rounded-xl p-4 animate-pulse h-64">
                <div className="h-6 bg-slate-700 rounded w-1/2 mb-4" />
                <div className="space-y-2">
                  <div className="h-4 bg-slate-700 rounded" />
                  <div className="h-4 bg-slate-700 rounded w-3/4" />
                  <div className="h-4 bg-slate-700 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <ChefHat className="w-16 h-16 text-slate-600 mb-4" />
            <p className="text-slate-400 text-lg">No orders in queue</p>
            <p className="text-slate-500 text-sm mt-1">New orders will appear here automatically</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {orders.map(order => (
              <KitchenOrderCard
                key={order.id}
                order={order}
                timeThresholds={TIME_THRESHOLDS}
                onBump={() => handleBumpOrder(order.id)}
                onStart={() => handleStartOrder(order.id)}
                selectedStation={selectedStation}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
